/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useCallback, useEffect, useRef } from 'react';
import { Provider } from 'react-redux';

import type { AiCoach_Action as AiCoachAction, AiCoach_Mode as AiCoachMode } from '__generated__/graphql-types';
import type { ApolloError } from 'apollo-client';

import { useLocation } from 'js/lib/useRouter';
import { get as getUser } from 'js/lib/user';
import UserAgent from 'js/lib/useragent';

import { breakpoints } from '@coursera/cds-core';
import type { CourseItem, Product } from '@coursera/event-pulse-types';

import Conversation from 'bundles/ai-course-coach/components/Conversation/Conversation';
import { COACH_RECOMMENDED_ACTION_CLICK } from 'bundles/ai-course-coach/components/Conversation/components/Messages';
import { useChatHistory } from 'bundles/ai-course-coach/components/ConversationHistory';
import { getErrorMessageText } from 'bundles/ai-course-coach/components/constants';
import store from 'bundles/ai-course-coach/store';
import {
  addResponseMessage,
  clearChatSession,
  closeCoachWidget,
  dropMessages,
  enableMsgLoader,
  openCoachWidget,
  sendMessage,
  setChatSessionId,
  setChatSessionStorageKey,
} from 'bundles/ai-course-coach/store/dispatcher';
import getCoachPendoTrigger from 'bundles/ai-course-coach/utils/coachPendoTrigger';
import createChatSessionStorageKey from 'bundles/ai-course-coach/utils/createChatSessionStorageKey';
import { persistantChatSessionId } from 'bundles/ai-course-coach/utils/persistantChatSessionId';
import { captureMessageInSentry } from 'bundles/ai-course-coach/utils/sentryUtils';
import { CoachTrackingDataProvider } from 'bundles/ai-course-coach/utils/tracking';
import type { HomeView } from 'bundles/ai-course-coach/utils/types';
import localStorageEx from 'bundles/common/utils/localStorageEx';
import sessionStorageEx from 'bundles/common/utils/sessionStorageEx';
import { useCourseToolBarPlugins } from 'bundles/course-v2/components/navigation/CourseToolBar';
import { useTracker } from 'bundles/page/lib/event-pulse/react';

import _t from 'i18n!nls/learning-assistant-chat-ai';

export const overrideStyles = () => {
  const result = css`
    z-index: 3100;
    bottom: 0;
    position: absolute;
    left: 0;
    top: 0;
    margin: 0 0 0 0;
    right: 0;
    padding: var(--cds-spacing-200);

    ${breakpoints.down('xs')} {
      padding: 0;
    }

    .coach-new-message {
      width: 100%;
    }

    .coach-input {
      height: 60px;
    }
  `;
  return result;
};

export type Props = {
  itemId?: string;
  courseId?: string;
  specializationId?: string;
  mode: AiCoachMode;
  cssOverrides?: ReturnType<typeof css>;
  homeView?: HomeView;
  recommendedActionsEnabled?: boolean;
  clipRecommendationsEnabled?: boolean;
  chatSessionKey?: string;
  onClose?: () => void;
};

function handleError(apolloError: ApolloError) {
  enableMsgLoader(false);
  addResponseMessage({ text: getErrorMessageText() });
  if (apolloError.graphQLErrors && apolloError.graphQLErrors?.length > 0) {
    const graphQLError = apolloError.graphQLErrors[0];
    captureMessageInSentry({ error: graphQLError });
  }
}

function isFirstImpression() {
  // ccfi = coursera coach first impression
  // using obscure value here to reduce the memory footprint. this may accidentally be abandoned
  // the goal is to avoid taking up more memory than necessary due to low level of importance and potental
  // for this to be abandoned
  const firstImpressionState = localStorageEx.getItem<boolean>('ccfi_embeddedCoach', (value) => value !== '1', true);

  if (firstImpressionState) {
    localStorageEx.setItem('ccfi_embeddedCoach', '1', (value) => value);
  }
  return firstImpressionState;
}

const coachPendoTriggerInstance = getCoachPendoTrigger();

/**
 * Main component for the Coach chat widget that is shown on several of our pages for
 * learners to interact with the Coach AI.
 */
const CoachChatEmbedded = ({
  itemId,
  courseId,
  recommendedActionsEnabled = false,
  clipRecommendationsEnabled = false,
  specializationId,
  mode,
  cssOverrides,
  homeView,
  chatSessionKey = '',
  onClose,
}: Props) => {
  const location = useLocation();
  const { sessionState } = store.getState();
  const isFirstRender = useRef<boolean>(true);
  const { setExpanded: setToolbarExpanded } = useCourseToolBarPlugins();
  const { chatSessionId: chatSessionIdState } = sessionState;

  const { loading, refreshHistory } =
    useChatHistory({
      chatSessionId: chatSessionIdState,
      historyLimit: 5,
      clipRecommendationsEnabled,
      recommendedActionsEnabled,
    }) || {};

  const track = useTracker();

  useEffect(() => {
    if (isFirstRender.current === true) {
      isFirstRender.current = false;

      const { isMobileBrowser } = new UserAgent(navigator.userAgent);

      // only default-show Coach if this is their first time seeing Coach
      // also don't default-show Coach on mobileweb since Coach is fullscreen [COACH-195]
      if (isFirstImpression() && !isMobileBrowser) {
        openCoachWidget();
        setToolbarExpanded(true);
      } else {
        // always keep Coach hidden on first mount otherwise
        closeCoachWidget();
      }
    }

    return () => {
      // close chat on unmount, such as when navigating to another page
      closeCoachWidget();
    };
  }, [track, setToolbarExpanded]);

  const getSessionStorageKey = useCallback(() => {
    const locationString = location.pathname + location.search + location.query;
    const sessionStorageKeyData = chatSessionKey || locationString;
    return createChatSessionStorageKey(sessionStorageKeyData);
  }, [location, chatSessionKey]);

  useEffect(() => {
    const sessionStorageKey = getSessionStorageKey();
    const sanitizedChatSessionId = persistantChatSessionId.get(sessionStorageKey);
    setChatSessionStorageKey(sessionStorageKey);

    setChatSessionId(sanitizedChatSessionId);
    dropMessages();

    if (sanitizedChatSessionId) {
      refreshHistory?.(sanitizedChatSessionId);
    } else {
      dropMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]); // TODO: adding `refreshHistory` as dependency results in infinite loop, needs more investigation

  const handleClose = () => {
    onClose?.();

    // used to notify ExternalCoachLauncher when Coach is closed
    document.body.dispatchEvent(new CustomEvent('coachChatClosed'));

    track('interact_coach', {
      coachInteractionType: 'close_coach_tool',
      coach: {
        mode,
        location: 'toolbar',
      },
      ...(courseId && {
        product: {
          type: 'course',
          id: courseId,
        } as Product,
      }),
      ...(itemId && {
        courseItem: {
          id: itemId,
        } as CourseItem,
      }),
    });
  };

  const handleNewSession = () => {
    // clear the chatSessionId from the session storage
    clearChatSession();
    // clear recommended action click timestamp from session storage
    sessionStorageEx.removeItem(COACH_RECOMMENDED_ACTION_CLICK);
    const sessionStorageKey = getSessionStorageKey();
    setChatSessionStorageKey(sessionStorageKey);
    dropMessages();
  };

  return (
    <div data-testid="coursera-coach-widget" css={[cssOverrides, overrideStyles]}>
      <Provider store={store}>
        <CoachTrackingDataProvider value={{ data: { mode, courseId, itemId, location: 'toolbar' } }}>
          <Conversation
            sendMessage={(message: string, action: AiCoachAction) => {
              // for each message send check if we need to initialize pendo (internal counter of how many times the function is called)
              coachPendoTriggerInstance.handleNewMessage();
              sendMessage({
                input: {
                  userId: String(getUser()?.id),
                  message,
                  courseraContext: {
                    specializationId,
                    courseId,
                    itemId,
                  },
                  mode,
                  action,
                },
                clipRecommendationsEnabled,
                recommendedActionsEnabled,
              }).catch(handleError);
            }}
            senderPlaceHolder={_t('Ask me anything')}
            profileClientAvatar={getUser().photo_120}
            toggleChat={handleClose} // always close because if you can see the coach close button, it will always close
            showCloseButton={true}
            showFullScreenButton={false} // hide on mobile since chat is always fullscreen when opened
            isFullScreen={false}
            disabledInput={false}
            autofocus={true}
            handleNewSession={handleNewSession}
            homeView={homeView}
            loading={loading}
            courseId={courseId}
          />
        </CoachTrackingDataProvider>
      </Provider>
    </div>
  );
};

CoachChatEmbedded.displayName = 'CoachChatEmbedded';

export default CoachChatEmbedded;
